import type {
  AccountCreationPresentationPayload,
  AccountCreationTextPayload,
  AccountRegistration,
} from '../gateway/contracts';
import { isCountryCatalog } from '../profile/Countries';
import { RULES_TEXT } from './CommunityRules';

type AccountCreationCallbacks = Readonly<{
  cancel: () => void;
  create: (registration: AccountRegistration) => Promise<AccountCreationOutcome>;
}>;

export type AccountCreationOutcome =
  | Readonly<{ created: true }>
  | Readonly<{
      created: false;
      field?: 'username' | 'email';
      message: string;
    }>;

type AccountStep = 1 | 2 | 3;
type AvatarGender = 0 | 1;
type AppearanceSelector = 'skin' | 'hair' | 'top' | 'pants';

const ACCOUNT_GARMENTS = {
  0: {
    skin: [122005, 122007, 122004, 122006, 122008, 122009, 122001, 122002, 122003],
    hair: [211041, 211042, 211043, 211044, 211045],
    top: [114018, 114019, 114020, 114021, 114022],
    pants: [116023, 116024, 116025, 116026, 116027],
  },
  1: {
    skin: [22004, 22005, 22006, 22007, 22008, 22009, 22001, 22002, 22003],
    hair: [1013, 1014, 1015, 1016, 1017, 1018],
    top: [14037, 14038, 14039, 14040, 14041],
    pants: [16014, 16015, 16016, 16017, 16018],
  },
} as const;

const ACCOUNT_THEMES = [
  { id: 1, name: 'winter' },
  { id: 2, name: 'summer' },
  { id: 6, name: 'night' },
  { id: 5, name: 'autumn' },
] as const;

const ENGLISH_ACCOUNT_TEXTS: AccountCreationTextPayload = {
  cancelLabel: 'Cancel',
  createAccountLabel: 'Create Account',
  boyLabel: 'Boy',
  girlLabel: 'Girl',
  skinLabel: 'Skin',
  hairLabel: 'Hair',
  topLabel: 'Top',
  pantsLabel: 'Pants',
  usernameLabel: 'Username',
  passwordLabel: 'Password',
  repeatPasswordLabel: 'Repeat password',
  emailLabel: 'Email',
  birthdayLabel: 'Birthday',
  yearPlaceholder: 'yyyy',
  monthPlaceholder: 'mm',
  dayPlaceholder: 'dd',
  agreementPrefix: 'I read and approved to',
  agreementLinkLabel: 'The Terms of Service',
  illegalUsernameMessage: 'Your username contains illegal characters.',
  usernameTakenMessage: 'The username is already taken.',
  emailTakenMessage: 'The email is already used by another user.',
};

const CURRENT_TERMS_VERSION = '2026-08-16';

export class AccountCreationView {
  readonly element: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly panel: HTMLElement;
  private readonly username: HTMLInputElement;
  private readonly password: HTMLInputElement;
  private readonly repeatedPassword: HTMLInputElement;
  private readonly email: HTMLInputElement;
  private readonly country: HTMLSelectElement;
  private countriesLoaded = false;
  private countriesLoading = false;
  private readonly birthYear: HTMLSelectElement;
  private readonly birthMonth: HTMLSelectElement;
  private readonly birthDay: HTMLSelectElement;
  private readonly terms: HTMLInputElement;
  private readonly termsDialog: HTMLDialogElement;
  private readonly rulesDialog: HTMLDialogElement;
  private readonly errorMessage: HTMLElement;
  private readonly previewLayers: readonly HTMLImageElement[];
  private presentation = ENGLISH_ACCOUNT_TEXTS;
  private termsAsset: AccountCreationPresentationPayload['termsAsset'] =
    '/legacy/startup/EULAENG.txt';
  private termsVersion = CURRENT_TERMS_VERSION;
  private step: AccountStep = 1;
  private gender: AvatarGender = 0;
  private selectedTheme: 1 | 2 | 5 | 6 = 2;
  private readonly appearanceIndices: Record<AppearanceSelector, number> = {
    skin: 0,
    hair: 0,
    top: 0,
    pants: 0,
  };

  constructor(private readonly callbacks: AccountCreationCallbacks) {
    this.element = document.createElement('section');
    this.element.className = 'account-creator';
    this.element.hidden = true;
    this.element.tabIndex = -1;
    this.element.setAttribute('aria-label', 'Create your Nord account');
    this.element.innerHTML = `
      <img class="account-creator__backdrop" src="/legacy/startup/createaccbackdrop.jpg" alt="" />
      <p class="account-creator__error" role="status" aria-live="polite"></p>
      <form class="account-creator__form" novalidate>
        <section class="account-creator__panel" aria-live="polite">
          <div class="account-creator__appearance">
            ${appearanceRowsMarkup()}
            <div class="account-avatar-preview" aria-label="Selected avatar preview">
              <img alt="" /><img alt="" /><img alt="" /><img alt="" />
            </div>
            <button class="account-small-button account-avatar-gender account-avatar-gender--boy" type="button">Boy</button>
            <button class="account-small-button account-avatar-gender account-avatar-gender--girl" type="button">Girl</button>
          </div>
          <div class="account-creator__details">
            ${detailsMarkup()}
          </div>
          <div class="account-creator__themes" aria-label="Village theme">
            ${ACCOUNT_THEMES.map(
              (theme) =>
                `<button class="account-theme account-theme--${theme.name}" type="button" data-theme="${String(theme.id)}" aria-label="${capitalize(theme.name)} village"><img alt="" /></button>`,
            ).join('')}
          </div>
          <div class="account-creator__tabs" aria-label="Account creation steps">
            <button class="account-tab account-tab--appearance" type="button" data-step="1" aria-label="Avatar appearance"></button>
            <button class="account-tab account-tab--details" type="button" data-step="2" aria-label="Account details"></button>
            <button class="account-tab account-tab--theme" type="button" data-step="3" aria-label="Village theme"></button>
          </div>
        </section>
        <button class="legacy-button account-creator__cancel" type="button">Cancel</button>
        <button class="legacy-button account-creator__submit" type="submit">Create Account</button>
      </form>
      <dialog class="account-terms-dialog" popover="manual" aria-label="The Terms of Service" aria-modal="false">
        <textarea aria-label="Terms of Service text" spellcheck="false" readonly>Loading the NordRevived Terms of Service…</textarea>
        <button class="account-terms-dialog__close" type="button" aria-label="Close terms">Close</button>
      </dialog>
      <dialog class="account-terms-dialog account-rules-dialog" popover="manual" aria-label="Rules &amp; Guidelines" aria-modal="false">
        <textarea aria-label="Rules &amp; Guidelines text" spellcheck="false" readonly></textarea>
        <button class="account-terms-dialog__close" type="button" aria-label="Close rules">Close</button>
      </dialog>
    `;
    this.form = requiredElement(this.element, '.account-creator__form', HTMLFormElement);
    this.panel = requiredElement(this.element, '.account-creator__panel', HTMLElement);
    this.username = requiredElement(this.element, '[name="accountUsername"]', HTMLInputElement);
    this.password = requiredElement(this.element, '[name="accountPassword"]', HTMLInputElement);
    this.repeatedPassword = requiredElement(
      this.element,
      '[name="repeatedPassword"]',
      HTMLInputElement,
    );
    this.email = requiredElement(this.element, '[name="email"]', HTMLInputElement);
    this.country = requiredElement(this.element, '[name="countryId"]', HTMLSelectElement);
    this.birthYear = requiredElement(this.element, '[name="birthYear"]', HTMLSelectElement);
    this.birthMonth = requiredElement(this.element, '[name="birthMonth"]', HTMLSelectElement);
    this.birthDay = requiredElement(this.element, '[name="birthDay"]', HTMLSelectElement);
    this.terms = requiredElement(this.element, '[name="terms"]', HTMLInputElement);
    this.termsDialog = requiredElement(this.element, '.account-terms-dialog', HTMLDialogElement);
    this.rulesDialog = requiredElement(this.element, '.account-rules-dialog', HTMLDialogElement);
    requiredElement(this.rulesDialog, 'textarea', HTMLTextAreaElement).value =
      `Rules & Guidelines\n\n${RULES_TEXT}`;
    this.errorMessage = requiredElement(this.element, '.account-creator__error', HTMLElement);
    this.previewLayers = [
      ...this.element.querySelectorAll<HTMLImageElement>('.account-avatar-preview img'),
    ];
    this.bindEvents();
    this.showStep(1);
    this.updateAppearance();
    this.updateThemeImages();
  }

  open(): void {
    this.element.hidden = false;
    void this.loadCountries();
    this.showStep(1);
    this.element.focus();
  }

  applyPresentation(presentation: AccountCreationPresentationPayload): void {
    this.presentation = presentation.texts;
    this.termsAsset = presentation.termsAsset;
    this.termsVersion = presentation.termsVersion;
    this.element.setAttribute('aria-label', presentation.texts.createAccountLabel);
    setText(this.element, '.account-creator__cancel', presentation.texts.cancelLabel);
    setText(this.element, '.account-creator__submit', presentation.texts.createAccountLabel);
    setText(this.element, '.account-avatar-gender--boy', presentation.texts.boyLabel);
    setText(this.element, '.account-avatar-gender--girl', presentation.texts.girlLabel);
    for (const selector of ['skin', 'hair', 'top', 'pants'] as const) {
      const label = presentation.texts[`${selector}Label`];
      setText(this.element, `[data-appearance-label="${selector}"]`, label);
    }
    for (const field of ['username', 'password', 'repeatPassword', 'email', 'birthday'] as const) {
      setText(this.element, `[data-account-label="${field}"]`, presentation.texts[`${field}Label`]);
    }
    updatePlaceholder(this.birthYear, presentation.texts.yearPlaceholder);
    updatePlaceholder(this.birthMonth, presentation.texts.monthPlaceholder);
    updatePlaceholder(this.birthDay, presentation.texts.dayPlaceholder);
    setText(this.element, '[data-account-label="agreement"]', presentation.texts.agreementPrefix);
    setText(this.element, '.account-terms-link', `${presentation.texts.agreementLinkLabel}.`);
    const termsOutput = requiredElement(this.termsDialog, 'textarea', HTMLTextAreaElement);
    delete termsOutput.dataset.loaded;
  }

  private close(): void {
    this.closeTerms();
    this.closeRules();
    this.element.hidden = true;
    this.callbacks.cancel();
  }

  private bindEvents(): void {
    requiredElement(this.element, '.account-creator__cancel', HTMLButtonElement).addEventListener(
      'click',
      () => this.close(),
    );
    for (const tab of this.element.querySelectorAll<HTMLButtonElement>('.account-tab[data-step]')) {
      tab.addEventListener('click', () => this.showStep(Number(tab.dataset.step) as AccountStep));
    }
    for (const button of this.element.querySelectorAll<HTMLButtonElement>('[data-selector]')) {
      button.addEventListener('click', () => {
        const selector = button.dataset.selector as AppearanceSelector;
        const direction = button.dataset.direction === 'previous' ? -1 : 1;
        this.rotateAppearance(selector, direction);
      });
    }
    requiredElement(
      this.element,
      '.account-avatar-gender--boy',
      HTMLButtonElement,
    ).addEventListener('click', () => this.setGender(0));
    requiredElement(
      this.element,
      '.account-avatar-gender--girl',
      HTMLButtonElement,
    ).addEventListener('click', () => this.setGender(1));
    for (const theme of this.element.querySelectorAll<HTMLButtonElement>('[data-theme]')) {
      theme.addEventListener('click', () => {
        this.selectedTheme = Number(theme.dataset.theme) as 1 | 2 | 5 | 6;
        this.updateThemeImages();
      });
    }
    requiredElement(this.element, '.account-terms-link', HTMLButtonElement).addEventListener(
      'click',
      () => void this.openTerms(),
    );
    requiredElement(
      this.element,
      '.account-terms-dialog__close',
      HTMLButtonElement,
    ).addEventListener('click', () => this.closeTerms());
    this.termsDialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closeTerms();
    });
    requiredElement(this.element, '.account-rules-link', HTMLButtonElement).addEventListener(
      'click',
      () => this.openRules(),
    );
    requiredElement(
      this.rulesDialog,
      '.account-terms-dialog__close',
      HTMLButtonElement,
    ).addEventListener('click', () => this.closeRules());
    this.rulesDialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closeRules();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submit();
    });
  }

  private async submit(): Promise<void> {
    if (!this.validateDetails()) {
      this.showStep(2);
      return;
    }
    const garments = ACCOUNT_GARMENTS[this.gender];
    this.setBusy(true);
    const outcome = await this.callbacks.create({
      username: this.username.value.trim(),
      password: this.password.value,
      email: this.email.value,
      countryId: Number(this.country.value),
      birthDate: Number(this.birthDay.value),
      birthMonth: Number(this.birthMonth.value),
      birthYear: Number(this.birthYear.value),
      sex: this.gender,
      hairGarmentId: indexedValue(garments.hair, this.appearanceIndices.hair),
      pantsGarmentId: indexedValue(garments.pants, this.appearanceIndices.pants),
      skinGarmentId: indexedValue(garments.skin, this.appearanceIndices.skin),
      topGarmentId: indexedValue(garments.top, this.appearanceIndices.top),
      villageTheme: this.selectedTheme,
      termsAccepted: true,
      termsVersion: this.termsVersion,
    });
    if (outcome.created) return;
    this.setBusy(false);
    this.errorMessage.textContent =
      outcome.field === 'username'
        ? this.presentation.usernameTakenMessage
        : outcome.field === 'email'
          ? this.presentation.emailTakenMessage
          : outcome.message;
    if (outcome.field !== undefined) {
      const marker = requiredElement(
        this.element,
        `[data-marker="${outcome.field === 'username' ? 'accountUsername' : 'email'}"]`,
        HTMLElement,
      );
      marker.hidden = false;
    }
    this.showStep(2);
  }

  private setBusy(busy: boolean): void {
    for (const button of this.form.querySelectorAll<HTMLButtonElement>('button')) {
      button.disabled = busy;
    }
    this.country.disabled = busy;
    this.birthYear.disabled = busy;
    this.birthMonth.disabled = busy;
    this.birthDay.disabled = busy;
  }

  private async loadCountries(): Promise<void> {
    if (this.countriesLoaded || this.countriesLoading) return;
    this.countriesLoading = true;
    try {
      const response = await fetch('/legacy/startup/countries.json');
      if (!response.ok) return;
      const value: unknown = await response.json();
      if (!isCountryCatalog(value)) return;
      this.country.append(
        ...value.countries.map((country) => new Option(country.name, String(country.id))),
      );
      this.countriesLoaded = true;
    } catch {
      // Country is optional; a failed catalog request must not block signup.
    } finally {
      this.countriesLoading = false;
    }
  }

  private showStep(step: AccountStep): void {
    this.step = step;
    this.panel.dataset.step = String(step);
    this.panel.style.backgroundImage = `url('/legacy/startup/${step === 1 ? 'shirt' : step === 2 ? 'edit' : 'tree'}bg.png')`;
    for (const tab of this.element.querySelectorAll<HTMLButtonElement>('.account-tab[data-step]')) {
      const tabStep = Number(tab.dataset.step);
      tab.hidden = tabStep === step;
      tab.setAttribute('aria-current', tabStep === step ? 'step' : 'false');
    }
    if (step === 2) this.username.focus();
  }

  private setGender(gender: AvatarGender): void {
    if (this.gender === gender) return;
    this.gender = gender;
    for (const selector of Object.keys(this.appearanceIndices) as AppearanceSelector[]) {
      this.appearanceIndices[selector] = 0;
    }
    this.updateAppearance();
  }

  private rotateAppearance(selector: AppearanceSelector, direction: number): void {
    const values = ACCOUNT_GARMENTS[this.gender][selector];
    this.appearanceIndices[selector] =
      (this.appearanceIndices[selector] + direction + values.length) % values.length;
    this.updateAppearance();
  }

  private updateAppearance(): void {
    const garments = ACCOUNT_GARMENTS[this.gender];
    const selected = [
      garments.skin[this.appearanceIndices.skin],
      garments.top[this.appearanceIndices.top],
      garments.hair[this.appearanceIndices.hair],
      garments.pants[this.appearanceIndices.pants],
    ];
    for (let index = 0; index < this.previewLayers.length; index++) {
      indexedValue(this.previewLayers, index).src =
        `/legacy/startup/bodyparts/${String(indexedValue(selected, index))}.png`;
    }
  }

  private updateThemeImages(): void {
    for (const theme of ACCOUNT_THEMES) {
      const button = requiredElement(
        this.element,
        `[data-theme="${String(theme.id)}"]`,
        HTMLButtonElement,
      );
      const selected = theme.id === this.selectedTheme;
      button.setAttribute('aria-pressed', String(selected));
      const image = requiredElement(button, 'img', HTMLImageElement);
      image.src = `/legacy/startup/${theme.name}_${selected ? 'active' : 'normal'}.png`;
      button.onmouseenter = () => {
        if (!selected) image.src = `/legacy/startup/${theme.name}_over.png`;
      };
      button.onmouseleave = () => {
        image.src = `/legacy/startup/${theme.name}_${selected ? 'active' : 'normal'}.png`;
      };
    }
  }

  private validateDetails(): boolean {
    const invalid = new Set<string>();
    this.errorMessage.textContent = '';
    const rawUsername = this.username.value;
    const acceptedUsername = rawUsername
      .trim()
      .slice(0, 16)
      .replace(/[^A-ZÅÄÖÆØa-zåäöæø0-9_]/g, '');
    if (acceptedUsername !== rawUsername) {
      invalid.add('accountUsername');
      this.errorMessage.textContent = this.presentation.illegalUsernameMessage;
    }
    if (acceptedUsername.length < 2) invalid.add('accountUsername');
    if (this.password.value.length < 3) invalid.add('accountPassword');
    if (this.repeatedPassword.value !== this.password.value) invalid.add('repeatedPassword');
    if (this.email.value.length < 3 || !this.email.value.includes('@')) invalid.add('email');
    if (this.birthYear.value === '' || this.birthMonth.value === '' || this.birthDay.value === '') {
      invalid.add('birthday');
    } else if (
      !validBirthDateForMinimumAge(
        Number(this.birthYear.value),
        Number(this.birthMonth.value),
        Number(this.birthDay.value),
        13,
        new Date(),
      )
    ) {
      invalid.add('birthday');
    }
    if (!this.terms.checked) invalid.add('terms');
    for (const marker of this.element.querySelectorAll<HTMLElement>('[data-marker]')) {
      marker.hidden = !invalid.has(marker.dataset.marker ?? '');
    }
    return invalid.size === 0;
  }

  private async openTerms(): Promise<void> {
    this.closeRules();
    if (!this.termsDialog.matches(':popover-open')) this.termsDialog.showPopover();
    const output = requiredElement(this.termsDialog, 'textarea', HTMLTextAreaElement);
    output.focus();
    if (output.dataset.loaded === 'true') {
      this.scrollTermsToSourceInitialPosition(output);
      return;
    }
    try {
      const response = await fetch(this.termsAsset);
      if (!response.ok) throw new Error('Terms unavailable');
      output.value = await response.text();
      output.dataset.loaded = 'true';
    } catch {
      output.value = 'The NordRevived Terms of Service could not be loaded.';
    }
    this.scrollTermsToSourceInitialPosition(output);
  }

  private scrollTermsToSourceInitialPosition(output: HTMLTextAreaElement): void {
    output.selectionStart = output.value.length;
    output.selectionEnd = output.value.length;
    requestAnimationFrame(() => {
      output.scrollTop = output.scrollHeight;
    });
  }

  private closeTerms(): void {
    if (this.termsDialog.matches(':popover-open')) this.termsDialog.hidePopover();
  }

  private openRules(): void {
    this.closeTerms();
    if (!this.rulesDialog.matches(':popover-open')) this.rulesDialog.showPopover();
    const output = requiredElement(this.rulesDialog, 'textarea', HTMLTextAreaElement);
    output.focus();
    output.setSelectionRange(0, 0);
    output.scrollTop = 0;
  }

  private closeRules(): void {
    if (!this.rulesDialog.matches(':popover-open')) return;
    this.rulesDialog.hidePopover();
    requiredElement(this.element, '.account-rules-link', HTMLButtonElement).focus();
  }
}

function appearanceRowsMarkup(): string {
  const rows: readonly AppearanceSelector[] = ['skin', 'hair', 'top', 'pants'];
  return rows
    .map(
      (name) => `<div class="account-appearance-row account-appearance-row--${name}">
        <button class="account-selector-arrow account-selector-arrow--left" type="button" data-selector="${name}" data-direction="previous" aria-label="Previous ${name}"></button>
        <span data-appearance-label="${name}">${capitalize(name)}</span>
        <button class="account-selector-arrow account-selector-arrow--right" type="button" data-selector="${name}" data-direction="next" aria-label="Next ${name}"></button>
      </div>`,
    )
    .join('');
}

function detailsMarkup(): string {
  return `
    <label class="account-detail account-detail--username"><span data-account-label="username">Username</span>:<input name="accountUsername" autocomplete="username" maxlength="20" /></label>
    <span class="account-invalid-marker account-invalid-marker--username" data-marker="accountUsername" hidden></span>
    <label class="account-detail account-detail--password"><span data-account-label="password">Password</span>:<input name="accountPassword" type="password" autocomplete="new-password" maxlength="256" /></label>
    <span class="account-invalid-marker account-invalid-marker--password" data-marker="accountPassword" hidden></span>
    <label class="account-detail account-detail--repeated-password"><span data-account-label="repeatPassword">Repeat password</span>:<input name="repeatedPassword" type="password" autocomplete="new-password" maxlength="256" /></label>
    <span class="account-invalid-marker account-invalid-marker--repeated-password" data-marker="repeatedPassword" hidden></span>
    <label class="account-detail account-detail--email"><span data-account-label="email">Email</span>:<input name="email" type="email" autocomplete="email" maxlength="254" /></label>
    <span class="account-invalid-marker account-invalid-marker--email" data-marker="email" hidden></span>
    <div class="account-detail account-detail--birthday"><span><span data-account-label="birthday">Birthday</span>:</span>
      <select name="birthYear" aria-label="Birth year"><option value="">yyyy</option>${birthYearOptions()}</select>
      <select name="birthMonth" aria-label="Birth month"><option value="">mm</option>${numberOptions(1, 12, 1, true)}</select>
      <select name="birthDay" aria-label="Birth day"><option value="">dd</option>${numberOptions(1, 31, 1, true)}</select>
    </div>
    <span class="account-invalid-marker account-invalid-marker--birthday" data-marker="birthday" hidden></span>
    <label class="account-detail account-detail--country">Country:<select name="countryId" aria-label="Country" aria-describedby="account-country-hint"><option value="">Prefer not to say</option></select></label>
    <p class="account-country-hint" id="account-country-hint">Suggests a language you can change on your profile.</p>
    <div class="account-terms-row">
      <label class="account-detail account-detail--terms"><input name="terms" type="checkbox" /> <span data-account-label="agreement">I read and approved to</span></label>
      <button class="account-terms-link" type="button">The Terms of Service.</button>
      <button class="account-rules-link" type="button" aria-haspopup="dialog">Rules &amp; Guidelines</button>
    </div>
    <span class="account-invalid-marker account-invalid-marker--terms" data-marker="terms" hidden></span>
  `;
}

function birthYearOptions(today = new Date()): string {
  return numberOptions(today.getFullYear() - 13, 1900, -1);
}

function validBirthDateForMinimumAge(
  year: number,
  month: number,
  day: number,
  minimumAge: number,
  today: Date,
): boolean {
  const birthDate = new Date(Date.UTC(year, month - 1, day));
  if (
    birthDate.getUTCFullYear() !== year ||
    birthDate.getUTCMonth() !== month - 1 ||
    birthDate.getUTCDate() !== day
  ) {
    return false;
  }
  const latestEligible = new Date(
    Date.UTC(today.getFullYear() - minimumAge, today.getMonth(), today.getDate()),
  );
  return birthDate.getTime() <= latestEligible.getTime();
}

function numberOptions(start: number, end: number, increment: number, padded = false): string {
  const result: string[] = [];
  for (let value = start; increment > 0 ? value <= end : value >= end; value += increment) {
    result.push(
      `<option value="${String(value)}">${padded ? String(value).padStart(2, '0') : String(value)}</option>`,
    );
  }
  return result.join('');
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function indexedValue<T>(values: readonly T[], index: number): T {
  const value = values[index];
  if (value === undefined) throw new Error(`Invalid account selection index: ${String(index)}`);
  return value;
}

function setText(root: ParentNode, selector: string, value: string): void {
  requiredElement(root, selector, HTMLElement).textContent = value;
}

function updatePlaceholder(select: HTMLSelectElement, value: string): void {
  const option = select.options.item(0);
  if (option !== null) option.textContent = value;
}

function requiredElement<T extends Element>(
  root: ParentNode,
  selector: string,
  constructor: new () => T,
): T {
  const element = root.querySelector(selector);
  if (!(element instanceof constructor)) throw new Error(`Missing account UI element: ${selector}`);
  return element;
}
